// Derive an Ulaanbaatar district name from a free-text address line.
//
// Branch/warehouse records store `district` as a postal code (e.g. "14000"),
// so the only place the human-readable дүүрэг survives is the free-text address
// ("Сүхбаатар дүүрэг 1-р хороо ..."). This mirrors the backend alias table in
// de-order-service/src/utils/district.ts so the web list shows the same district
// the route-naming / pull-sheet logic derives.

export const DISTRICT_ALIASES: Record<string, string> = {
  "БГД"           : "Баянгол",
  "БАЯНГОЛ"       : "Баянгол",
  "СБД"           : "Сүхбаатар",
  "СҮХБААТАР"     : "Сүхбаатар",
  "ХУД"           : "Хан-Уул",
  "ХАН-УУЛ"       : "Хан-Уул",
  "ХАН УУЛ"       : "Хан-Уул",
  "БЗД"           : "Баянзүрх",
  "БАЯНЗҮРХ"      : "Баянзүрх",
  "ЧД"            : "Чингэлтэй",
  "ЧИНГЭЛТЭЙ"     : "Чингэлтэй",
  "СХД"           : "Сонгинохайрхан",
  "СОНГИНО"       : "Сонгинохайрхан",
  "СОНГИНОХАЙРХАН": "Сонгинохайрхан",
  "НД"            : "Налайх",
  "НАЛАЙХ"        : "Налайх",
  "БГНУ"          : "Багануур",
  "БАГАНУУР"      : "Багануур",
  "БГХА"          : "Багахангай",
  "БАГАХАНГАЙ"    : "Багахангай",
}

// Multi-character names are matched as substrings (distinctive enough); short
// abbreviations (≤ 3 chars, e.g. "ЧД", "НД", "БГД") are matched only as whole
// tokens so they don't false-match inside an unrelated word. Longer aliases are
// tried first so a full name wins over a coincidental abbreviation.
export function extractDistrictName(text: string | null | undefined): string | null {
  if (!text) return null
  const up = text.toUpperCase()
  const tokens = new Set(up.split(/[^А-ЯӨҮЁ-]+/).filter(Boolean))
  const aliases = Object.keys(DISTRICT_ALIASES).sort((a, b) => b.length - a.length)
  for (const alias of aliases) {
    const matched = alias.length <= 3 ? tokens.has(alias) : up.includes(alias)
    if (matched) return DISTRICT_ALIASES[alias]
  }
  return null
}

const POSTAL_CODE_RE = /^\d{4,6}$/

export function districtLabel(
  branch?: { district?: string | null; branchAddress?: string | null } | null,
  warehouse?: { district?: string | null; warehouseAddress?: string | null } | null,
): string | null {
  const structured = [branch?.district, warehouse?.district]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .find((s) => s.length > 0 && !POSTAL_CODE_RE.test(s))
  if (structured) return structured

  const extracted =
    extractDistrictName(warehouse?.warehouseAddress) ?? extractDistrictName(branch?.branchAddress)
  if (extracted) return extracted

  const raw = (branch?.district ?? warehouse?.district ?? "").trim()
  return raw || null
}
